import { differenceInCalendarDays } from 'date-fns';
import { TFunction } from 'i18next';

import { SubscriptionStatus, UserSubscription } from 'types';

export const isTrialExpiredSubscription = (
  subscription?: UserSubscription | null,
): boolean => {
  return subscription?.status === SubscriptionStatus.TRIAL_EXPIRED;
};

/** Active free trial (not expired). */
export const isTrialSubscription = (
  subscription?: UserSubscription | null,
): boolean => {
  if (!subscription) return false;
  if (isTrialExpiredSubscription(subscription)) return false;
  return (
    subscription.planId === SubscriptionStatus.TRIAL ||
    subscription.status === SubscriptionStatus.TRIAL
  );
};

export const getRemainingTrialDays = (
  subscription?: UserSubscription | null,
  now = new Date(),
): number | null => {
  if (!isTrialSubscription(subscription) || !subscription?.endDate) {
    return null;
  }

  const trialEnd = new Date(subscription.endDate);
  if (Number.isNaN(trialEnd.getTime()) || trialEnd.getTime() <= now.getTime()) {
    return null;
  }

  return differenceInCalendarDays(trialEnd, now);
};

export const getAnnualFreeMonths = (
  monthlyValue: number,
  yearlySaveVsMonthly: number,
): number => {
  if (!monthlyValue || yearlySaveVsMonthly <= 0) return 0;
  return Math.max(1, Math.round(yearlySaveVsMonthly / monthlyValue));
};

type FormatFreePeriodLabelParams = {
  remainingTrialDays?: number | null;
  annualFreeMonths?: number;
};

export const formatFreePeriodLabel = (
  t: TFunction,
  { remainingTrialDays, annualFreeMonths = 0 }: FormatFreePeriodLabelParams,
): string => {
  if (remainingTrialDays != null && remainingTrialDays > 0) {
    if (remainingTrialDays >= 30) {
      const months = Math.max(1, Math.floor(remainingTrialDays / 30));
      if (months === 1) {
        return t('changePlan.saveBadgeOneMonth');
      }
      return t('changePlan.saveBadgeMonths', { count: months });
    }

    return t('changePlan.saveBadgeDays', { count: remainingTrialDays });
  }

  if (annualFreeMonths > 0) {
    if (annualFreeMonths === 1) {
      return t('changePlan.saveBadgeOneMonth');
    }
    return t('changePlan.saveBadgeMonths', { count: annualFreeMonths });
  }

  return t('changePlan.saveBadge');
};
